import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { getPartnerAuthHeaders } from "@/lib/apiAuth";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Rocket, RotateCcw } from "lucide-react";
import PartnerSelect from "@/pages/LandingPages/marketingAgency/shared/PartnerSelect";
import RequestAiEmployeeModal from "./RequestAiEmployeeModal";
import { AI_TIERS } from "@/constants/aiTiersCatalog";
import { LANDING_PATHS } from "@/lib/landingPaths";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VERTICAL_OPTIONS = [
  {
    value: "roofing",
    label: "Roofing AI Employee",
    learnMoreUrl: LANDING_PATHS.roofing,
  },
  {
    value: "real_estate",
    label: "Real Estate AI Employee",
    learnMoreUrl: LANDING_PATHS.realEstate,
  },
];

const INITIAL_FORM = {
  clientName: "",
  contactEmail: "",
  vertical: "roofing",
  tier: "1",
};

const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

const DeployWizardForm = ({ onRequestSubmitted }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  const getAuthHeader = useCallback(() => getPartnerAuthHeaders(), []);

  useEffect(() => {
    if (!getPartnerAuthHeaders().Authorization) {
      setBillingLoading(false);
      return;
    }
    axios
      .get(`${BACKEND_URL}/api/partner/billing/summary`, {
        headers: getPartnerAuthHeaders(),
      })
      .then((res) => setBilling(res.data))
      .catch(() => setBilling(null))
      .finally(() => setBillingLoading(false));
  }, []);

  const canDeploy = billing?.can_deploy === true;
  const billingBlocked = !billingLoading && billing && !canDeploy;

  const validate = () => {
    const next = {};
    if (!form.clientName.trim() || form.clientName.trim().length < 2) {
      next.clientName = "Client name is required (min 2 characters)";
    }
    if (!form.contactEmail.trim()) {
      next.contactEmail = "Contact email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail)) {
      next.contactEmail = "Enter a valid email address";
    }
    if (!form.tier) {
      next.tier = "Select a tier";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setSubmitError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || isSubmitting || !canDeploy) return;

    setIsSubmitting(true);
    setSubmitError("");
    try {
      await axios.post(
        `${BACKEND_URL}/api/partner/deploy`,
        {
          client_name: form.clientName.trim(),
          contact_email: form.contactEmail.trim(),
          vertical: form.vertical,
          tier: Number(form.tier),
        },
        { headers: getAuthHeader() }
      );
      toast.success("Client deployed successfully");
      navigate("/partner/clients");
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      const message = extractErrorMessage(err, "Deploy failed. Please try again.");
      setSubmitError(message);
      if (err?.response?.status === 402) {
        toast.error(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setSubmitError("");
  };

  const selectedVertical = VERTICAL_OPTIONS.find((option) => option.value === form.vertical);
  const productOverviewUrl = selectedVertical?.learnMoreUrl;

  return (
    <div className="rounded-2xl border border-partner bg-[#11151F] p-6 sm:p-8">
      {billingBlocked && (
        <div
          className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-start gap-3"
          data-testid="partner-deploy-billing-gate"
        >
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-200">Payment method required</p>
            <p className="text-sm text-amber-100/80 font-body mt-1">
              Add a payment method in Partner Billing before deploying clients. Wholesale charges
              apply automatically on each deploy.
            </p>
            <Link
              to="/partner/billing"
              className="inline-block mt-2 text-sm font-semibold text-partner-accent hover:underline"
            >
              Go to Partner Billing
            </Link>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} data-testid="partner-deploy-form" className="space-y-8">
        <section>
          <h2 className="text-sm font-mono tracking-wider text-partner-accent uppercase mb-4">
            Step 1: Client identity
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="clientName" className="block text-xs text-partner-muted font-body mb-1.5">
                Client display name *
              </label>
              <input
                id="clientName"
                type="text"
                value={form.clientName}
                onChange={handleChange("clientName")}
                className="w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body"
                placeholder="Summit Roofing Co."
              />
              {errors.clientName && (
                <p className="mt-1 text-xs text-red-400">{errors.clientName}</p>
              )}
            </div>
            <div>
              <label htmlFor="contactEmail" className="block text-xs text-partner-muted font-body mb-1.5">
                Contact email *
              </label>
              <input
                id="contactEmail"
                type="email"
                value={form.contactEmail}
                onChange={handleChange("contactEmail")}
                className="w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body"
                placeholder="ops@client.com"
              />
              {errors.contactEmail && (
                <p className="mt-1 text-xs text-red-400">{errors.contactEmail}</p>
              )}
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-mono tracking-wider text-partner-accent uppercase mb-4">
            Step 2: Product & tier
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="vertical" className="block text-xs text-partner-muted font-body mb-1.5">
                AI Employee
              </label>
              <PartnerSelect
                id="vertical"
                value={form.vertical}
                onChange={handleChange("vertical")}
                options={VERTICAL_OPTIONS}
                placeholder="Select AI Employee"
              />
              {productOverviewUrl && (
                <p className="mt-2 text-sm text-partner-muted font-body">
                  <a
                    href={productOverviewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-partner-accent font-semibold hover:underline"
                    data-testid="partner-ai-employee-overview-link"
                  >
                    See what this AI Employee does
                  </a>
                  <span> · opens in new tab</span>
                </p>
              )}
              <p className="mt-2 text-sm text-partner-muted font-body">
                Don&apos;t see your client&apos;s industry?{" "}
                <button
                  type="button"
                  onClick={() => setRequestModalOpen(true)}
                  className="text-partner-accent font-semibold hover:underline"
                  data-testid="partner-request-ai-employee-open"
                >
                  Request a new AI Employee
                </button>
              </p>
            </div>
            <div className="sm:col-span-2">
              <p className="block text-xs text-partner-muted font-body mb-2">Infrastructure tier *</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {AI_TIERS.map((tier) => (
                  <label
                    key={tier.id}
                    className={`flex flex-col gap-1 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                      form.tier === String(tier.id)
                        ? "border-[#00D2FF]/40 bg-[#00D2FF]/10 text-partner-accent"
                        : "border-partner text-partner-muted hover:border-[#00D2FF]/20"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="tier"
                        value={String(tier.id)}
                        checked={form.tier === String(tier.id)}
                        onChange={handleChange("tier")}
                        className="accent-[#00D2FF]"
                      />
                      <span className="text-sm font-body font-semibold">{tier.deployLabel}</span>
                    </span>
                    <span className="text-xs font-body text-partner-muted pl-6">
                      {tier.credits.toLocaleString()} AI Credits included
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-partner-muted font-body">
                Wholesale charge applies immediately on deploy (prorated for the current billing cycle).
              </p>
              {errors.tier && <p className="mt-1 text-xs text-red-400">{errors.tier}</p>}
            </div>
          </div>
        </section>

        {submitError && (
          <p className="text-sm text-red-400 font-body" data-testid="partner-deploy-error">
            {submitError}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={isSubmitting || billingLoading || !canDeploy}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#00D2FF] text-[#080A0F] font-semibold text-sm hover:bg-[#00D2FF]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="partner-deploy-submit"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Rocket className="w-4 h-4" />
            )}
            {isSubmitting ? "Deploying…" : "Deploy client"}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={isSubmitting}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-partner text-partner-muted font-body text-sm hover:text-partner hover:bg-[#080A0F] transition-colors disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" />
            Reset form
          </button>
        </div>
      </form>

      <RequestAiEmployeeModal
        open={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        onSuccess={() => onRequestSubmitted?.()}
      />
    </div>
  );
};

export default DeployWizardForm;
